import React, { useState, useRef, useEffect } from 'react';
import { useMultiChain } from '../contexts/MultiChainProvider';
import { ChevronDown, ChevronUp, Check, Coins, Zap } from 'lucide-react';

const ChainSelector = () => {
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowEVMChains(false);
        setShowSolanaNetworks(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const {
    selectedChain,
    setSelectedChain,
    selectedEVMChain,
    setSelectedEVMChain,
    selectedSolanaNetwork,
    setSelectedSolanaNetwork,
    EVM_CHAINS,
    SOLANA_NETWORKS,
    switchEVMChain,
    library,
  } = useMultiChain();

  const [isOpen, setIsOpen] = useState(false);
  const [showEVMChains, setShowEVMChains] = useState(false);
  const [showSolanaNetworks, setShowSolanaNetworks] = useState(false);

  const handleChainSelect = (chainType) => {
    console.log('Selecting chain type:', chainType);
    setSelectedChain(chainType);
    setIsOpen(false);
    setShowEVMChains(false);
    setShowSolanaNetworks(false);
  };

  const handleEVMChainSelect = async (chainId) => {
    console.log('Selecting EVM chain:', chainId);
    if (library) {
      await switchEVMChain(chainId);
    } else {
      setSelectedEVMChain(chainId);
    }
    setShowEVMChains(false);
  };

  const handleSolanaNetworkSelect = (network) => {
    console.log('Selecting Solana network:', network);
    setSelectedSolanaNetwork(network);
    setShowSolanaNetworks(false);
  };

  const getCurrentChainInfo = () => {
    if (selectedChain === 'evm') {
      const chain = EVM_CHAINS[selectedEVMChain];
      return {
        name: chain?.name || 'Ethereum',
        icon: <Coins className="w-4 h-4" />,
        color: 'text-blue-400',
      };
    } else {
      const network = SOLANA_NETWORKS[selectedSolanaNetwork];
      return {
        name: network?.name || 'Solana',
        icon: <Zap className="w-4 h-4" />,
        color: 'text-green-400',
      };
    }
  };

  const currentChain = getCurrentChainInfo();

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 sm:space-x-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
      >
        <div className={currentChain.color}>
          {selectedChain === 'evm' ? (
            <Coins className="w-3 h-3 sm:w-4 sm:h-4" />
          ) : (
            <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
          )}
        </div>
        <span className="text-white hidden sm:inline">{currentChain.name}</span>
        <span className="text-white sm:hidden">{selectedChain === 'evm' ? 'EVM' : 'SOL'}</span>
        {isOpen ? (
          <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 sm:w-72 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-[9999] max-h-80 sm:max-h-96 overflow-y-auto">
          <div className="p-2">
            {/* Chain Type Selection */}
            <div className="space-y-1">
              <button
                onClick={() => handleChainSelect('solana')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 ${
                  selectedChain === 'solana'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : 'hover:bg-gray-700/50 text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Zap className="w-4 h-4 text-green-400" />
                  <span>Solana</span>
                </div>
                {selectedChain === 'solana' && <Check className="w-4 h-4" />}
              </button>

              <button
                onClick={() => handleChainSelect('evm')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                  selectedChain === 'evm'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'hover:bg-gray-700/50 text-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Coins className="w-4 h-4 text-blue-400" />
                  <span>EVM Chains</span>
                </div>
                {selectedChain === 'evm' && <Check className="w-4 h-4" />}
              </button>
            </div>

            {/* Solana Networks */}
            {selectedChain === 'solana' && (
              <div className="mt-3 pt-3 border-t border-gray-600">
                <button
                  onClick={() => setShowSolanaNetworks(!showSolanaNetworks)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-400 hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50"
                >
                  <span>Networks</span>
                  {showSolanaNetworks ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
                {showSolanaNetworks && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(SOLANA_NETWORKS).map(([key, network]) => (
                      <button
                        key={key}
                        onClick={() => handleSolanaNetworkSelect(key)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-500/50 ${
                          selectedSolanaNetwork === key
                            ? 'bg-green-500/20 text-green-400'
                            : 'hover:bg-gray-700/50 text-gray-300'
                        }`}
                      >
                        <span>{network.name}</span>
                        {selectedSolanaNetwork === key && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* EVM Chains */}
            {selectedChain === 'evm' && (
              <div className="mt-3 pt-3 border-t border-gray-600">
                <button
                  onClick={() => setShowEVMChains(!showEVMChains)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-400 hover:text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                >
                  <span>Chains</span>
                  {showEVMChains ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                
                {showEVMChains && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                    {Object.entries(EVM_CHAINS).map(([chainId, chain]) => (
                      <button
                        key={chainId}
                        onClick={() => handleEVMChainSelect(parseInt(chainId))}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                          selectedEVMChain === parseInt(chainId)
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'hover:bg-gray-700/50 text-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Coins className="w-4 h-4" />
                          <span>{chain.name}</span>
                        </div>
                        {selectedEVMChain === parseInt(chainId) && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChainSelector; 